import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useSticker } from '../context/StickerContext';
import { LeftToolRail, ToolType } from '../components/editor/LeftToolRail';
import { CanvasToolbar } from '../components/editor/CanvasToolbar';
import { EditorCanvas } from '../components/editor/EditorCanvas';
import { CutlinePanel } from '../components/editor/CutlinePanel';
import { ShapePanel } from '../components/editor/ShapePanel';
import { SizePanel } from '../components/editor/SizePanel';
import { QRCodePanel } from '../components/editor/QRCodePanel';
import { QuantityPanel } from '../components/editor/QuantityPanel';
import { FiltersPanel } from '../components/editor/FiltersPanel';
import { TextPanel } from '../components/editor/TextPanel';
import { ClipartPanel } from '../components/editor/ClipartPanel';
import { PrimaryButton, SecondaryButtonOutline } from '../components/ui/Buttons';
import { Share2, X } from 'lucide-react';
function fmt(n: number) {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  });
}
export function EditorPage() {
  const navigate = useNavigate();
  const {
    totalPrice,
    quantity
  } = useSticker();
  const [activeTool, setActiveTool] = useState<ToolType>('cutline');
  const [zoom, setZoom] = useState(50);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [note, setNote] = useState('');
  const [savedNote, setSavedNote] = useState('');
  // Cutline State
  const [showInnerCutline, setShowInnerCutline] = useState(true);
  const [showOuterCutline, setShowOuterCutline] = useState(true);
  const [previewCutlines, setPreviewCutlines] = useState(true);
  const [activeCutline, setActiveCutline] = useState<'inner' | 'outer'>('outer');
  const handleZoomIn = () => setZoom(z => Math.min(z + 25, 200));
  const handleZoomOut = () => setZoom(z => Math.max(z - 25, 25));
  const handleZoomReset = () => setZoom(50);
  const handleSaveNote = () => {
    setSavedNote(note);
    setShowNoteModal(false);
  };
  const renderActivePanel = () => {
    switch (activeTool) {
      case 'cutline':
        return <CutlinePanel showInnerCutline={showInnerCutline} setShowInnerCutline={setShowInnerCutline} showOuterCutline={showOuterCutline} setShowOuterCutline={setShowOuterCutline} activeCutline={activeCutline} setActiveCutline={setActiveCutline} data-id="element-467" />;
      case 'size':
        return <SizePanel data-id="element-468" />;
      case 'qrcode':
        return <QRCodePanel data-id="element-469" />;
      case 'quantity':
        return <QuantityPanel data-id="element-470" />;
      case 'filters':
        return <FiltersPanel data-id="element-471" />;
      case 'text':
        return <TextPanel data-id="element-472" />;
      case 'clipart':
        return <ClipartPanel data-id="element-473" />;
      default:
        return <ShapePanel data-id="element-474" />;
    }
  };
  return <div className="h-screen flex flex-col bg-white overflow-hidden" data-id="element-475">
      <header className="h-14 border-b border-[#D9D9D9] flex items-center justify-between px-4 bg-white z-30" data-id="element-476">
        <span className="text-sm font-medium text-[#5F5F5F]" data-id="element-477">
          Design 1 · Custom Sticker
        </span>
        <div className="flex items-center gap-4" data-id="element-478">
          <div className="text-right hidden md:block" data-id="element-479">
            <span className="text-xs text-[#5F5F5F] block" data-id="element-480">
              {quantity > 0 ? quantity.toLocaleString() : '—'} stickers
            </span>
            <span className="text-sm font-bold text-[#111111]" data-id="element-481">
              {totalPrice > 0 ? fmt(totalPrice) : '—'}
            </span>
          </div>
          <SecondaryButtonOutline className="!py-1.5 !px-3 text-sm flex items-center gap-2" data-id="element-482">
            <Share2 size={14} data-id="element-483" />
            Share
          </SecondaryButtonOutline>
          <PrimaryButton className="!py-1.5 !px-4 text-sm" onClick={() => navigate('/stickers/proof')} data-id="element-484">
            Confirm
          </PrimaryButton>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden" data-id="element-485">
        <LeftToolRail activeTool={activeTool} onToolChange={setActiveTool} data-id="element-486" />
        <div className="w-80 bg-white border-r border-[#D9D9D9] h-full z-10 shadow-[4px_0_24px_rgba(0,0,0,0.02)] relative overflow-hidden" data-id="element-487">
          <AnimatePresence mode="wait" data-id="element-488">
            <motion.div key={activeTool} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2,
            ease: 'easeInOut'
          }} className="h-full w-full absolute inset-0" data-id="element-489">
              {renderActivePanel()}
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex-1 flex flex-col relative bg-gray-100" data-id="element-490">
          <div className="absolute top-4 right-4 z-20 flex items-center gap-4" data-id="element-491">
            <div className="flex gap-4 text-sm font-medium" data-id="element-492">
              <button onClick={() => {
              setNote(savedNote);
              setShowNoteModal(true);
            }} className="text-[#008696] hover:text-[#006D78] relative" data-id="element-493">
                Add Note
                {savedNote && <span className="absolute -top-1 -right-2 w-2 h-2 bg-[#008696] rounded-full" data-id="element-494" />}
              </button>
            </div>
            <CanvasToolbar zoom={zoom} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onZoomReset={handleZoomReset} previewCutlines={previewCutlines} onTogglePreviewCutlines={() => setPreviewCutlines(!previewCutlines)} data-id="element-495" />
          </div>
          <EditorCanvas zoom={zoom} showInnerCutline={showInnerCutline} showOuterCutline={showOuterCutline} previewCutlines={previewCutlines} activeCutline={activeCutline} onCutlineClick={setActiveCutline} data-id="element-496" />
        </div>
      </div>

      {/* Add Note Modal */}
      <AnimatePresence data-id="element-497">
        {showNoteModal && <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-id="element-498">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowNoteModal(false)} data-id="element-499" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full relative z-10" data-id="element-500">
              <div className="flex items-center justify-between mb-4" data-id="element-501">
                <h2 className="text-lg font-semibold text-[#111111]" data-id="element-502">
                  Add a Note
                </h2>
                <button onClick={() => setShowNoteModal(false)} className="p-1 hover:bg-gray-100 rounded-full text-[#5F5F5F]" data-id="element-503">
                  <X size={18} data-id="element-504" />
                </button>
              </div>
              <p className="text-sm text-[#5F5F5F] mb-3" data-id="element-505">
                Add any special instructions or notes for your sticker order.
              </p>
              <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Please ensure the colors match Pantone 185 C..." rows={5} className="w-full rounded-lg border border-[#D9D9D9] px-4 py-3 text-sm focus:ring-2 focus:ring-[#008696] focus:border-[#008696] outline-none resize-none" data-id="element-506" />
              <div className="flex justify-end gap-3 mt-4" data-id="element-507">
                {savedNote && <button onClick={() => {
              setNote('');
              setSavedNote('');
              setShowNoteModal(false);
            }} className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-508">
                    Remove Note
                  </button>}
                <SecondaryButtonOutline className="!py-2 !px-4 text-sm" onClick={() => setShowNoteModal(false)} data-id="element-509">
                  Cancel
                </SecondaryButtonOutline>
                <PrimaryButton className="!py-2 !px-4 text-sm" onClick={handleSaveNote} data-id="element-510">
                  Save Note
                </PrimaryButton>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}